import {actionTool, objectInput, readyForPerson, requireFilled, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {fillInput, formatPhoneForInput, press, pressButtonWithText, setChecked} from "@/functions/webMcpDom";
import {TERMS_REASON} from "@/functions/webMcpToolsSignIn";
import {buildWhileDuplicateEmailShows, resolveDuplicateEmail} from "@/functions/webMcpToolsCreateAccount";
import getBrandName from "@/functions/getBrandName";

const DUPLICATE_EMAIL_CHOICES = {
	sign_in: 'sign in.',
	recover_old_account: 'Recover my old account',
	create_anyway: 'Create new account with this email',
}

const buildLinkClientAccount = () => actionTool({
	name: 'link_client_account',
	description: `Get linking a client's existing ${getBrandName()} account to the signed-in preparer account ready. Enters the client's username; the client types their own password into the page, never through this tool. Linking can't be undone and agrees to the Terms of Use, so the tool stops there and returns ready_for_person: the person presses Link account, then confirms or cancels the "Are you sure?" dialog themselves.`,
	inputSchema: objectInput({
		clientUsername: stringInput('The client\'s username, exactly as the client gave it.', {maxLength: 128}),
	}, ['clientUsername']),
	run: ({clientUsername}) => {
		const missing = requireFilled({clientUsername}, {clientUsername: 'Enter the client\'s username.'})
		if (missing) return missing
		fillInput('#username', clientUsername, 'client username field')
		return readyForPerson('[data-testid="link-account"]', {secrets: ['#password'], reason: `linking can't be undone, so the person confirms it in the "Are you sure?" dialog, and ${TERMS_REASON}`})
	},
})

const buildCancelLinkClient = () => actionTool({
	name: 'cancel_link_client',
	description: 'Cancel linking a client account and return the preparer to the preparer portal, as the page\'s Cancel button does. Nothing is linked and the client\'s password is not sent. The browser leaves the sign-in site, so the next page to read is the preparer portal itself.',
	run: () => pressButtonWithText('Cancel'),
})

const buildCreatePreparerAccount = () => actionTool({
	name: 'create_preparer_account',
	description: `Fill the new ${getBrandName()} tax preparer account form with the details the person gave you. The password goes into both password fields from the person or their password manager, never through this tool. Pressing Create new account agrees to the Terms of Use, so the tool stops there and returns ready_for_person; the person presses it, and the site runs its captcha.`,
	inputSchema: objectInput({
		email: stringInput('The preparer\'s email address.', {maxLength: 75}),
		username: stringInput('The username the preparer wants.', {maxLength: 128}),
		phone: stringInput('Optional 10-digit US phone number, for example 8015551234.'),
	}, ['email', 'username']),
	run: ({email, username, phone}) => {
		const missing = requireFilled({email, username}, {email: 'Enter the preparer\'s email address.', username: 'Enter the username the preparer wants.'})
		if (missing) return missing
		fillInput('#email', email, 'email address field')
		fillInput('#confirmEmail', email, 'confirm email address field')
		fillInput('#username', username, 'username field')
		if (phone) fillInput('#phone', formatPhoneForInput(phone), 'phone number field')
		return readyForPerson('#submitCreatePreparerUser', {secrets: ['#password', '#confirmPassword'], reason: TERMS_REASON})
	},
})

const buildResolveDuplicateEmail = buildWhileDuplicateEmailShows(() => actionTool({
	name: 'resolve_duplicate_email',
	description: 'The page says this email address already has an account. Choose what to do: sign in to that account, recover the old account, or create a new account with the same email anyway. Signing in and recovering are pressed for you; creating a new account agrees to the Terms of Use, so that one returns ready_for_person for the person to press.',
	inputSchema: objectInput({choice: stringInput('What the person wants to do.', {enum: Object.keys(DUPLICATE_EMAIL_CHOICES)})}, ['choice']),
	run: resolveDuplicateEmail(DUPLICATE_EMAIL_CHOICES, 'create_anyway'),
}))

const buildSubmitVerificationCode = () => actionTool({
	name: 'submit_verification_code',
	description: 'Press Continue once the preparer has typed the code from their email into the page. The code never passes through this tool, so it waits until the field is filled. Returns the next step, usually the preparer portal, or the page\'s message if the code is wrong or expired; resend_verification_code sends another.',
	inputSchema: objectInput({
		rememberDevice: {type: 'boolean', description: 'Whether to remember this device so it skips the code next time. Defaults to true.'},
	}),
	run: ({rememberDevice = true}) => {
		const waiting = waitForSecrets('submit_verification_code', ['#verify-code'])
		if (waiting) return waiting
		setChecked('#rememberMe', rememberDevice, 'Remember this device checkbox')
		return press('button[type="submit"][form="verifyEmailForm"]', 'Continue button')
	},
})

const buildResendVerificationCode = () => actionTool({
	name: 'resend_verification_code',
	description: 'Send a new verification code to the preparer\'s email address, as the Send New Code button does. Use it when the first code did not arrive or expired. Returns the page\'s message confirming the new code or explaining why it could not be sent, such as too many requests.',
	run: () => pressButtonWithText('Send New Code'),
})

const buildCreateClient = () => actionTool({
	name: 'create_client',
	description: `Fill the new-client form for a client of the signed-in ${getBrandName()} tax preparer with the client's first and last name. Pressing Create client agrees to the Terms of Use, so the tool stops there and returns ready_for_person for the preparer to press. Empty names come back as invalid_fields with the field id; the page's own name checks run when the preparer presses the button.`,
	inputSchema: objectInput({
		firstName: stringInput('The client\'s first name.', {maxLength: 18}),
		lastName: stringInput('The client\'s last name.', {maxLength: 25}),
	}, ['firstName', 'lastName']),
	run: ({firstName, lastName}) => {
		const missing = requireFilled({firstName, lastName}, {firstName: 'Enter the client\'s first name.', lastName: 'Enter the client\'s last name.'})
		if (missing) return missing
		fillInput('#firstName', firstName, 'first name field')
		fillInput('#lastName', lastName, 'last name field')
		return readyForPerson('form button.sign-in[type="submit"]', {reason: TERMS_REASON})
	},
})

export const preparerTools = {
	LinkClientAccount: [buildLinkClientAccount, buildCancelLinkClient],
	CreatePreparerAccount: [buildCreatePreparerAccount, buildResolveDuplicateEmail],
	PreparerVerifyEmail: [buildSubmitVerificationCode, buildResendVerificationCode],
	CreateClientAccount: [buildCreateClient],
}